/**
 * MatterAccessPanel — per-matter access controls for the barrister, on a case page.
 *
 * Two things (docs/04-ENGINEERING/CAPABILITIES/access-control.md):
 *   1. Matter Visibility — Normal / Restricted / Private (the confidentiality seal).
 *   2. Per-clerk, per-capability exceptions for THIS matter (Allow / Deny / Default),
 *      which override the clerk's blanket setting just for this case.
 *
 * Backend: /me/clerk-access. Every change is audited server-side.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CircleNotch } from '@phosphor-icons/react';
import { fetchTeam, type TeamMember } from '@/lib/teamApi';
import {
  fetchMatterAccess,
  setMatterVisibility,
  setClerkMatterOverride,
  clearClerkMatterOverride,
} from '@/lib/clerkAccessApi';

type Visibility = 'personal' | 'team' | 'chambers' | 'restricted';
type OverrideState = 'ALLOW' | 'DENY' | 'DEFAULT';

const MATTER_CAPS: { key: string; label: string }[] = [
  { key: 'matters', label: 'Matter' },
  { key: 'documents', label: 'Documents' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'emails', label: 'Emails' },
  { key: 'fees', label: 'Fees' },
];

const VIS_OPTIONS: { value: Visibility; label: string; hint: string }[] = [
  { value: 'team', label: 'Normal', hint: 'Follows each clerk’s usual access' },
  { value: 'restricted', label: 'Restricted', hint: 'Hidden from clerks unless allowed below' },
  { value: 'personal', label: 'Private', hint: 'Barrister only — no clerk, ever' },
];

export function MatterAccessPanel({
  matterId,
  designatedClerkUserId,
  onVisibilityChange,
}: {
  matterId: string;
  /** The matter's designated clerk (matter.clerkUserId). Case exceptions apply to this
   *  clerk only — with no designated clerk there is nobody to configure. */
  designatedClerkUserId?: string | null;
  onVisibilityChange?: () => void;
}) {
  const [clerks, setClerks] = useState<TeamMember[]>([]);
  const [visibility, setVisibility] = useState<Visibility>('team');
  // key `${clerkId}:${capability}` -> 'ALLOW' | 'DENY'
  const [overrides, setOverrides] = useState<Record<string, 'ALLOW' | 'DENY'>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    Promise.all([fetchMatterAccess(matterId), fetchTeam()])
      .then(([access, team]) => {
        if (!active) return;
        setVisibility(access.visibility);
        const map: Record<string, 'ALLOW' | 'DENY'> = {};
        for (const o of access.overrides) map[`${o.clerkId}:${o.capability}`] = o.effect;
        setOverrides(map);
        setClerks(team.filter((m) => m.memberType === 'senior_clerk' || m.memberType === 'junior_clerk'));
      })
      .catch(() => toast.error('Could not load matter access'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [matterId]);

  const changeVisibility = async (v: Visibility) => {
    const prev = visibility;
    setVisibility(v);
    setBusy(true);
    try {
      await setMatterVisibility(matterId, v);
      toast.success('Visibility updated');
      onVisibilityChange?.();
    } catch {
      setVisibility(prev);
      toast.error('Failed to update visibility');
    } finally {
      setBusy(false);
    }
  };

  const setOverride = async (clerkId: string, capability: string, next: OverrideState) => {
    const key = `${clerkId}:${capability}`;
    const prev = overrides[key];
    // optimistic
    setOverrides((m) => {
      const copy = { ...m };
      if (next === 'DEFAULT') delete copy[key];
      else copy[key] = next;
      return copy;
    });
    setBusy(true);
    try {
      if (next === 'DEFAULT') await clearClerkMatterOverride(clerkId, matterId, capability);
      else await setClerkMatterOverride(clerkId, matterId, capability, next);
    } catch {
      setOverrides((m) => ({ ...m, ...(prev ? { [key]: prev } : {}) }));
      toast.error('Failed to update exception');
    } finally {
      setBusy(false);
    }
  };

  // Case exceptions apply to the matter's designated clerk only.
  const designatedClerk = designatedClerkUserId
    ? clerks.find((c) => c.userId === designatedClerkUserId)
    : undefined;

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border p-4 text-xs text-muted-foreground">
        <CircleNotch size={14} className="animate-spin" /> Loading access…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Matter visibility</h3>
        <div className="mt-2 flex flex-wrap gap-2">
          {VIS_OPTIONS.map((o) => (
            <button
              key={o.value}
              disabled={busy}
              onClick={() => changeVisibility(o.value)}
              title={o.hint}
              className={
                'rounded-md border px-3 py-1.5 text-[11px] font-semibold transition disabled:opacity-50 ' +
                (visibility === o.value
                  ? o.value === 'personal'
                    ? 'border-red-400 bg-red-500 text-white'
                    : o.value === 'restricted'
                      ? 'border-amber-400 bg-amber-500 text-white'
                      : 'border-emerald-400 bg-emerald-500 text-white'
                  : 'bg-background text-muted-foreground hover:bg-muted')
              }
            >
              {o.label}
            </button>
          ))}
        </div>
        <p className="mt-1 text-[10px] text-muted-foreground">{VIS_OPTIONS.find((o) => o.value === visibility)?.hint}</p>
      </div>

      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Clerk exceptions for this case</h3>
        {/* Private is absolute — no clerk (not even the designated clerk) can be granted in.
            Otherwise, case exceptions apply to the DESIGNATED clerk only; with none assigned
            there is nobody to configure. */}
        {visibility === 'personal' ? (
          <p className="mt-2 rounded-md border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-2 py-1.5 text-[11px] text-red-800 dark:text-red-300">
            This matter is <strong>Private</strong> — barrister only. No clerk can access it, not even the
            designated clerk. Switch to <strong>Restricted</strong> if you need to allow a specific clerk in.
          </p>
        ) : !designatedClerk ? (
          <p className="mt-1 text-[11px] text-muted-foreground">
            No designated clerk assigned. Assign one above to set case-specific access.
          </p>
        ) : (
          <div className="mt-2 space-y-3">
            {visibility === 'restricted' && (
              <p className="text-[11px] text-muted-foreground">
                <strong>Restricted</strong> — hidden from clerks unless you <strong>Allow</strong> them on this case.
              </p>
            )}
            {[designatedClerk].map((c) => (
              <div key={c.userId} className="rounded-md border p-2.5">
                <div className="mb-1.5 text-xs font-medium text-foreground">{c.firstName} {c.lastName}</div>
                <div className="space-y-1">
                  {MATTER_CAPS.map((cap) => {
                    const current = (overrides[`${c.userId}:${cap.key}`] ?? 'DEFAULT') as OverrideState;
                    return (
                      <div key={cap.key} className="flex items-center justify-between gap-2">
                        <span className="text-[11px] text-muted-foreground">{cap.label}</span>
                        <div className="flex overflow-hidden rounded border">
                          {(['ALLOW', 'DENY', 'DEFAULT'] as OverrideState[]).map((s) => (
                            <button
                              key={s}
                              disabled={busy}
                              onClick={() => setOverride(c.userId, cap.key, s)}
                              className={
                                'px-2 py-0.5 text-[10px] font-semibold transition disabled:opacity-50 ' +
                                (current === s
                                  ? s === 'ALLOW'
                                    ? 'bg-emerald-500 text-white'
                                    : s === 'DENY'
                                      ? 'bg-red-500 text-white'
                                      : 'bg-muted-foreground/70 text-white'
                                  : 'bg-background text-muted-foreground hover:bg-muted')
                              }
                            >
                              {s === 'DEFAULT' ? 'Default' : s === 'ALLOW' ? 'Allow' : 'Deny'}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
